/**
 * useHomeActions — the route map for every home module (PPL-417; spec §4
 * "Actions", the issue's table) and the executor behind it.
 *
 * Modules PROPOSE: they emit a semantic `HomeModuleAction` — `open` for the
 * primary tap, a verb (`review`, `rsvp`, `rotate_match`, `dismiss`,
 * `expand`) for everything else. This file DISPOSES: `HOME_ROUTES` holds one
 * rule per `HomeModuleType` that turns `open` into an intent — a route (+
 * the router state the target reads), a composer prefill, a thread to
 * resume — and `homeIntent` maps the verbs onto the callables and the desk
 * state the hook then runs. `HOME_ROUTES` is typed `Record<HomeModuleType,
 * …>`, so a module type without a primary handler is a compile error, not a
 * dead tap.
 *
 * Register is the server's: every name here is a payload field used as
 * given. The prefill templates are HOME_STATIC's. Nothing re-ranks.
 *
 *   module        primary                                     secondary / inline
 *   M3 review     /insights                                   keep / skip → respondToSuggestionEurW · undo → undoWritebackItemEurW · a keep that needs a choice → /insights
 *   M4 today      prefill the opener's reply (else the today line; post-event line after the event)
 *   M5 dates      prefill "help me write a birthday note to {name}"  via 'circle' → /circles/:slug (inSharedCircle)
 *   M6 checkin    prefill "help me check in on {name}"        "not now" → dismiss (14 d, server)
 *   M7 continue   loadThreadMessages(threadId)
 *   M8 meet       /user/:matchUserId (their profile)          "someone else" → dismiss the match + silent re-fetch
 *   M9 clear_air  /rooms?compose=existing + { personId } · paused → /rooms/:id   dismiss (30 d, server)
 *   M10 rooms     /rooms/:id/<step> from payload.screen · else /rooms/:id · multi → /rooms
 *   M11 circles   /circles/:slug/chat · /circles/:slug/events/:eventId · /circles/:slug   rsvp → rsvpCircleEventEurW
 *   M12 posts     /talk/:slug
 *   M13 week      /insights
 *   M14 memory    /contacts + { peopleFilter }
 *   M15 setup     app-only → /download · notifications / photo / handle → /settings, /profile/edit   dismiss
 *   M16 welcome   expands in place (the module owns it)
 *   M17 trial     /premium
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { CirclesPayload, HomeFeed, HomeModule, HomeModuleType, RoomsPayload, SetupPayload } from '../../types/home';
import type { HomeModuleAction, HomeOpenVia } from './moduleTypes';
import type { HomeInlineState, ReviewInline, RsvpInlineStatus } from './inlineState';
import { HOME_STATIC } from './homeStatic';
import { slugify } from '../../utils/circleSlug';
import { rsvpToEvent } from '../../services/circleService';
import { useWriteback } from '../../contexts/WritebackContext';
import type { SuggestionItem } from '../../services/writebackService';

// ── intents ───────────────────────────────────────────────────────────

export type HomeIntent =
  | { kind: 'navigate'; to: string; state?: Record<string, unknown> }
  /** Put text in the composer and focus it. Never auto-sends. */
  | { kind: 'prefill'; text: string }
  /** M7 — resume a thread in place. */
  | { kind: 'thread'; threadId: string }
  | { kind: 'dismiss'; dismissKey: string }
  /** M8 "someone else": dismiss the served match (no undo row) and re-fetch for the next face. */
  | { kind: 'rotate'; dismissKey: string }
  | { kind: 'review'; itemId: string; decision: 'keep' | 'skip' | 'undo' }
  | { kind: 'rsvp'; circleId: string; eventId: string }
  /** Re-fetch the feed (M8 "someone else"). */
  | { kind: 'refresh' }
  | { kind: 'none' };

const NONE: HomeIntent = { kind: 'none' };

function go(to: string, state?: Record<string, unknown>): HomeIntent {
  return state ? { kind: 'navigate', to, state } : { kind: 'navigate', to };
}

/** A prefill with nothing to say (no name in the payload) is a no-op, never an empty composer. */
function say(text: string): HomeIntent {
  return text ? { kind: 'prefill', text } : NONE;
}

// ── route helpers (pure, exported for the modules that share them) ────

/** Circle routes are slug-addressed with the id in router state (useCircleId reads state first). */
export function circleSlug(p: { slug: string | null; circleName: string | null; circleId: string }): string {
  return p.slug || (p.circleName ? slugify(p.circleName) : '') || p.circleId;
}

export function circleRoute(p: CirclesPayload): { to: string; state: { circleId: string } } {
  const slug = circleSlug(p);
  const state = { circleId: p.circleId };
  if (p.variant === 'event' && p.event) return { to: `/circles/${slug}/events/${p.event.id}`, state };
  if (p.variant === 'unread') return { to: `/circles/${slug}/chat`, state };
  return { to: `/circles/${slug}`, state };
}

/** The mediation route leaves web has (App.jsx `/rooms/:mediationId/<leaf>`). */
const ROOM_WEB_LEAVES: ReadonlySet<string> = new Set(['invite', 'tell-me-more', 'prep', 'consent', 'waiting', 'aside', 'opener', 'room', 'checkin', 'end', 'summary']);

/**
 * `payload.screen` is the MediationStack step the app rail uses; these are
 * the app's MediationNavigator names (and the card handler's spellings)
 * mapped to the web leaf. Anything else falls to /rooms/:id, the catch-all
 * that opens the room at its current step.
 */
const ROOM_APP_STEP_LEAF: Readonly<Record<string, string>> = {
  MediationInviteReceived: 'invite',
  MediationTellMore: 'tell-me-more',
  MediationPrep: 'prep',
  MediationConsent: 'consent',
  MediationWaiting: 'waiting',
  MediationAside: 'aside',
  MediationOpener: 'opener',
  MediationRoom: 'room',
  MediationCheckin: 'checkin',
  MediationEnd: 'end',
  MediationSummary: 'summary',
  RoomInviteReceived: 'invite',
  RoomPrep: 'prep',
  RoomConsent: 'consent',
  RoomSession: 'room',
  RoomCheckin: 'checkin',
  RoomEnd: 'end',
  RoomSummary: 'summary',
};

export function roomRoute(p: RoomsPayload): string {
  if (p.variant === 'multi' || !p.mediationId) return '/rooms';
  let leaf = ROOM_WEB_LEAVES.has(p.screen) ? p.screen : ROOM_APP_STEP_LEAF[p.screen] ?? (p.variant === 'invite' ? 'invite' : '');
  // A `waiting` room still in prep is "my prep done, theirs not". The app
  // rail names the prep step for it, but web's own rooms list (RoomList
  // routeFor → inWaitingRoom) opens the waiting room — the plain prep page
  // would show the form the user has already finished and not redirect.
  if (leaf === 'prep' && p.variant === 'waiting') leaf = 'waiting';
  return leaf ? `/rooms/${p.mediationId}/${leaf}` : `/rooms/${p.mediationId}`;
}

/** M15's non-app-only kinds open the existing settings / profile screens. */
export const SETUP_ROUTE: Readonly<Record<SetupPayload['kind'], string>> = {
  contacts: '/contacts',
  calendar: '/settings',
  notifications: '/settings',
  photo: '/profile/edit',
  handle: '/profile/edit',
};

/**
 * M4 on web (O1-b, no life surface): the opener's reply when the feed has
 * one, else the today line — or, after the event, the "how it went" line —
 * for the first name the payload carries.
 */
export function todayPrefillFor(feed: HomeFeed | null, module: HomeModule): string {
  if (module.type !== 'today') return '';
  const p = module.payload;
  const name = p.personNames[0] ?? p.several?.next.personName ?? '';
  // After the event the row asks "how was it?" — the opener's reply, written
  // before it, answers the wrong question; the post-event line is the reply.
  if (p.postEvent && name) return HOME_STATIC.postEventPrefill(name);
  const reply = feed?.opener?.prompt;
  if (reply) return reply;
  return name ? HOME_STATIC.todayPrefill(name) : '';
}

/**
 * Whether M3's inline keep must open the review screen instead of sending a
 * bare `accept` — the rules Insights.tsx applies before it sends one. An
 * identity item with candidates needs the "who is this?" choice:
 * respondToSuggestionEurW answers a bare accept on it by CREATING a new
 * person, a duplicate of the candidate it should have linked. An item with
 * no target person that is not a `new_person` needs assigning first (the
 * server refuses it; a bare `calendar_attendee` also needs a name). An item
 * not in the pending page (subscription not ready, settled elsewhere) is
 * sent as a bare accept and the server says what happened.
 */
export function needsReviewScreen(item: Pick<SuggestionItem, 'kind' | 'candidates' | 'personId' | 'type'>): boolean {
  if (item.kind === 'identity' && item.candidates.length > 0) return true;
  return !item.personId && item.type !== 'new_person';
}

// ── the route map: one primary rule per module type ───────────────────

type ModuleOf<T extends HomeModuleType> = Extract<HomeModule, { type: T }>;
type OpenRule<T extends HomeModuleType> = (module: ModuleOf<T>, feed: HomeFeed | null, via?: HomeOpenVia) => HomeIntent;

export const HOME_ROUTES: { readonly [T in HomeModuleType]: OpenRule<T> } = {
  // M3 — the review screen. keep / skip / undo arrive as the `review` verb, not `open`.
  review: () => go('/insights'),
  // M4 — the composer, never a route: web has no calendar surface.
  today: (m, feed) => say(todayPrefillFor(feed, m)),
  // M5 — a note through f8; "plan with the circle" only when they share one.
  dates: (m, _feed, via) => {
    const p = m.payload;
    if (via === 'circle') {
      if (!p.inSharedCircle || !p.circleId) return NONE;
      return go(`/circles/${circleSlug({ slug: null, circleName: p.circleName, circleId: p.circleId })}`, { circleId: p.circleId });
    }
    return say(HOME_STATIC.datesPrefill(p.kind, p.personName));
  },
  // M6 — the existing "ask f8 about X" path. "not now" is the `dismiss` verb (14 d, server-side).
  checkin: (m) => say(HOME_STATIC.checkinPrefill(m.payload.personName)),
  // M7 — the thread itself, in place.
  continue: (m) => ({ kind: 'thread', threadId: m.payload.threadId }),
  // M8 — the match's public profile (PPL-425): the person before the ask, and
  // both ways in ("invite to a room", "send a message") live there already.
  // `from: 'home_match'` keeps room_created attributed 'match', not 'profile'.
  // ANONYMOUS register: the profile is handle-only; no name travels.
  meet: (m) => go(`/user/${m.payload.matchUserId}`, { from: 'home_match' }),
  // M9 — the offer opens the composer with the person preselected (RoomList
  // reads `personId`); a paused room resumes at its current step.
  clear_air: (m) => (m.payload.variant === 'paused' && m.payload.mediationId ? go(`/rooms/${m.payload.mediationId}`) : go('/rooms?compose=existing', { personId: m.payload.personId })),
  // M10
  rooms: (m) => go(roomRoute(m.payload)),
  // M11 — chat / event / circle by variant. rsvp is the `rsvp` verb.
  circles: (m) => {
    const r = circleRoute(m.payload);
    return go(r.to, r.state);
  },
  // M12
  posts: (m) => go(`/talk/${m.payload.slug}`),
  // M13
  week: () => go('/insights'),
  // M14 — the remembered list (useRememberList's chips), landing on the bucket the module is about.
  memory: (m) => go('/contacts', { peopleFilter: m.payload.variant === 'paused' ? 'paused' : 'active' }),
  // M15 — contacts / calendar are app-only on web: the download page. (WebAppChip is a Link there itself.)
  setup: (m) => go(m.payload.appOnly ? '/download' : SETUP_ROUTE[m.payload.kind]),
  // M16 — expands in place; the module owns it and reports `expand`, never `open`.
  welcome_back: () => NONE,
  // M17
  trial: () => go('/premium'),
};

/** Compile-time completeness: a HomeModuleType without a rule fails here, not on a tap. */
type MissingRoute = Exclude<HomeModuleType, keyof typeof HOME_ROUTES>;
const routesComplete: MissingRoute extends never ? true : never = true;
void routesComplete;

export function openIntent(module: HomeModule, feed: HomeFeed | null, via?: HomeOpenVia): HomeIntent {
  // Correlated union: TS cannot see that HOME_ROUTES[module.type] takes
  // exactly this module, so the lookup is widened to the union rule. A type
  // this build does not know (the registry renders it as nothing anyway)
  // has no rule and no destination.
  const rule = HOME_ROUTES[module.type] as OpenRule<HomeModuleType> | undefined;
  return rule ? rule(module, feed, via) : NONE;
}

function assertNever(x: never): never {
  throw new Error(`useHomeActions: unhandled action ${JSON.stringify(x)}`);
}

/** Every action a module can emit, as the intent the host runs. */
export function homeIntent(action: HomeModuleAction, module: HomeModule, feed: HomeFeed | null): HomeIntent {
  switch (action.kind) {
    case 'open':
      return openIntent(module, feed, action.via);
    case 'dismiss':
      return { kind: 'dismiss', dismissKey: action.dismissKey };
    case 'review':
      return { kind: 'review', itemId: action.itemId, decision: action.decision };
    case 'rsvp':
      return { kind: 'rsvp', circleId: action.circleId, eventId: action.eventId };
    case 'rotate_match':
      return { kind: 'rotate', dismissKey: module.dismissKey };
    case 'expand':
      return NONE;
    default:
      return assertNever(action);
  }
}

/** The analytics `target` of a tap (§11 home_module_tapped): the resolved destination, not the proposal. */
export function intentTarget(intent: HomeIntent, action: HomeModuleAction): string {
  switch (intent.kind) {
    case 'navigate':
      return `navigate:${intent.to}`;
    case 'review':
      return `review:${intent.decision}`;
    case 'none':
      return action.kind === 'expand' ? `expand:${action.expanded ? 'open' : 'close'}` : action.kind;
    default:
      return intent.kind;
  }
}

// ── the executor ──────────────────────────────────────────────────────

export interface HomeActionsHost {
  navigate: (to: string, state?: Record<string, unknown>) => void;
  /** Put text in the composer and focus it. Never auto-sends. */
  prefill: (text: string) => void;
  /**
   * M7 — resume a thread in place (useF8Chat's loadThreadMessages). Without
   * it the page is sent to /f8chat with `{ threadId }`, which F8Chat honours.
   */
  openThread?: (threadId: string) => void;
  /** The desk's optimistic dismiss (useHomeDesk.dismiss → homeDismissEurW). */
  dismiss: (module: HomeModule, opts?: { silent?: boolean }) => void;
  /** Re-fetch the feed after a server-side mutation. */
  refresh: () => void;
  feed: HomeFeed | null;
}

export interface HomeActions {
  /** The intent an action resolves to — computed before anything runs, so analytics can name the target. */
  resolve: (action: HomeModuleAction, module: HomeModule) => HomeIntent;
  execute: (intent: HomeIntent, module: HomeModule) => void;
  /** What the inline controls should show (HomeInlineContext). */
  inline: HomeInlineState;
}

/**
 * How long after a keep / skip / undo settles the feed is re-read so the
 * count and the first item move on. Longer than the review queue's own
 * deferred-reject window, so a skip has actually landed before the feed is
 * asked about it, and long enough that the receipt ("kept · undo") is seen.
 */
export const REFRESH_AFTER_REVIEW_MS = 8_000;

export function useHomeActions(host: HomeActionsHost): HomeActions {
  // M3 goes through the app-wide review queue (WritebackContext): the same
  // deferred reject with its undo window and pagehide flush the /insights
  // page uses, and the same receipt overlay — a keep on home reads as kept
  // on /insights too.
  const wb = useWriteback();
  const [rsvp, setRsvp] = useState<Record<string, RsvpInlineStatus>>({});
  const [failed, setFailed] = useState<Record<string, true>>({});
  const hostRef = useRef(host);
  hostRef.current = host;
  const refreshTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cancelRefresh = useCallback(() => {
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = null;
  }, []);
  const scheduleRefresh = useCallback(
    (ms: number = REFRESH_AFTER_REVIEW_MS) => {
      cancelRefresh();
      refreshTimer.current = setTimeout(() => {
        refreshTimer.current = null;
        hostRef.current.refresh();
      }, ms);
    },
    [cancelRefresh],
  );
  useEffect(() => cancelRefresh, [cancelRefresh]);

  const clearFailed = useCallback((itemId: string) => {
    setFailed((f) => {
      if (!f[itemId]) return f;
      const next = { ...f };
      delete next[itemId];
      return next;
    });
  }, []);

  const review = useCallback(
    (itemId: string, decision: 'keep' | 'skip' | 'undo') => {
      clearFailed(itemId);
      if (decision === 'keep') {
        void wb.resolve(itemId, 'accept').then((result) => {
          // null = the call failed and the overlay was put back; say so
          // instead of pretending. Anything else settled one way or another.
          if (!result) setFailed((f) => ({ ...f, [itemId]: true }));
          else scheduleRefresh();
        });
        return;
      }
      if (decision === 'skip') {
        wb.dismiss(itemId);
        scheduleRefresh();
        return;
      }
      const state = wb.resolved[itemId];
      if (!state) return;
      if (state.status === 'dismissed') {
        // Inside the window the reject has not gone out yet — cancelling it
        // IS the undo, and there is nothing for the feed to catch up on.
        if (wb.undoDismiss(itemId)) cancelRefresh();
        return;
      }
      if (state.status === 'accepted' && state.undoable && !state.pendingCall) {
        void wb.undo(itemId).then((ok) => {
          if (ok) scheduleRefresh();
        });
      }
    },
    [cancelRefresh, clearFailed, scheduleRefresh, wb],
  );

  const doRsvp = useCallback(
    (circleId: string, eventId: string) => {
      setRsvp((r) => {
        const cur = r[eventId];
        return cur === 'pending' || cur === 'going' ? r : { ...r, [eventId]: 'pending' };
      });
      rsvpToEvent(circleId, eventId, 'going')
        .then((res) => {
          if (res && res.success === false) throw new Error('rsvpCircleEventEurW: success=false');
          setRsvp((r) => ({ ...r, [eventId]: 'going' }));
          hostRef.current.refresh();
        })
        .catch((err) => {
          console.warn(`useHomeActions: rsvp ${eventId} failed`, err);
          setRsvp((r) => ({ ...r, [eventId]: 'failed' }));
        });
    },
    [],
  );

  const resolve = useCallback(
    (action: HomeModuleAction, module: HomeModule): HomeIntent => {
      // A keep the review screen itself would not send as a bare accept (a
      // person to choose, to assign, or to name) goes to the review screen,
      // where those controls are. Decided here rather than in homeIntent so
      // the analytics target names the real destination.
      if (action.kind === 'review' && action.decision === 'keep') {
        const item = wb.pending.find((i) => i.id === action.itemId);
        if (item && needsReviewScreen(item)) return go('/insights');
      }
      return homeIntent(action, module, hostRef.current.feed);
    },
    [wb.pending],
  );

  const execute = useCallback(
    (intent: HomeIntent, module: HomeModule) => {
      const h = hostRef.current;
      switch (intent.kind) {
        case 'navigate':
          h.navigate(intent.to, intent.state);
          return;
        case 'prefill':
          h.prefill(intent.text);
          return;
        case 'thread':
          if (h.openThread) h.openThread(intent.threadId);
          else h.navigate('/f8chat', { threadId: intent.threadId });
          return;
        case 'dismiss':
          h.dismiss(module);
          return;
        case 'rotate':
          h.dismiss(module, { silent: true });
          return;
        case 'review':
          review(intent.itemId, intent.decision);
          return;
        case 'rsvp':
          doRsvp(intent.circleId, intent.eventId);
          return;
        case 'refresh':
          h.refresh();
          return;
        case 'none':
          return;
        default:
          assertNever(intent);
      }
    },
    [doRsvp, review],
  );

  const inline = useMemo<HomeInlineState>(() => {
    const reviewFor = (itemId: string): ReviewInline | null => {
      if (failed[itemId]) return { status: 'failed', undoable: false };
      const r = wb.resolved[itemId];
      if (!r) return null;
      switch (r.status) {
        case 'accepted':
          return { status: 'kept', undoable: r.undoable && !r.pendingCall };
        case 'dismissed':
          return { status: 'skipped', undoable: r.undoable };
        case 'undone':
          return { status: 'undone', undoable: false };
        case 'conflicted':
          return { status: 'conflicted', undoable: false };
        default:
          return null;
      }
    };
    return { review: reviewFor, rsvp: (eventId) => rsvp[eventId] ?? null };
  }, [failed, rsvp, wb.resolved]);

  return useMemo(() => ({ resolve, execute, inline }), [resolve, execute, inline]);
}
